import React, { useRef, useEffect } from 'react';

// Logo A — Star & Circle
export function LogoA({ size = 64, color = '#c9956e' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="21" stroke={color} strokeWidth="0.8" opacity="0.5" />
      <path
        d="M24 7 L25.4 22.6 L40 24 L25.4 25.4 L24 41 L22.6 25.4 L8 24 L22.6 22.6 Z"
        fill={color}
      />
      <circle cx="24" cy="24" r="1.8" fill={color} opacity="0.6" />
    </svg>
  );
}

// Logo B — Horizon Rising
export function LogoB({ size = 64, color = '#c9956e' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <line x1="9" y1="42" x2="24" y2="11" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="39" y1="42" x2="24" y2="11" stroke={color} strokeWidth="1.3" strokeLinecap="round" />
      <line x1="3" y1="31.5" x2="45" y2="31.5" stroke={color} strokeWidth="0.7" strokeLinecap="round" opacity="0.45" />
      <circle cx="24" cy="11" r="5.5" stroke={color} strokeWidth="1" fill="none" />
      <circle cx="24" cy="11" r="1.6" fill={color} />
    </svg>
  );
}

// Logo C — Golden Spiral
export function LogoC({ size = 64, color = '#c9956e' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M 11 32 A 16 16 0 0 1 27 16 A 10 10 0 0 1 37 26 A 6 6 0 0 1 31 32 A 4 4 0 0 1 27 28 A 2 2 0 0 1 29 26"
        stroke={color}
        strokeWidth="1.2"
        fill="none"
        strokeLinecap="round"
      />
      <circle cx="29" cy="26" r="1.4" fill={color} />
    </svg>
  );
}

// Logo D — Crescent Moon
export function LogoD({ size = 64, color = '#c9956e' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M 24 8 A 16 16 0 1 1 24 40 A 11 11 0 1 0 24 8 Z"
        fill={color}
        opacity="0.85"
      />
      <circle cx="35" cy="13" r="1" fill={color} opacity="0.5" />
      <circle cx="39" cy="22" r="0.7" fill={color} opacity="0.4" />
      <circle cx="37" cy="30" r="0.8" fill={color} opacity="0.35" />
    </svg>
  );
}

// Logo E — Saturn (ringed planet)
export function LogoE({ size = 64, color = '#c9956e' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="24" cy="24" r="9.5" stroke={color} strokeWidth="1" fill="none" />
      <ellipse cx="24" cy="24" rx="18" ry="6" stroke={color} strokeWidth="0.9" fill="none" opacity="0.75" />
      <path
        d="M 14.6 24 A 9.5 9.5 0 0 1 33.4 24"
        stroke="#0a0b12"
        strokeWidth="4"
        fill="none"
        strokeLinecap="butt"
      />
      <path
        d="M 6 24 A 18 6 0 0 0 42 24"
        stroke={color}
        strokeWidth="0.9"
        fill="none"
        opacity="0.75"
      />
      <circle cx="24" cy="24" r="1.5" fill={color} opacity="0.5" />
    </svg>
  );
}

// LogoShowcase — infinite marquee driven by requestAnimationFrame (no CSS stutter)
// Left-to-right direction, seamless loop via duplicated items.
export function LogoShowcase({ heroMode = false }) {
  const options = [
    { Component: LogoD, label: 'Crescent' },
    { Component: LogoA, label: 'Star & Circle' },
    { Component: LogoB, label: 'Horizon Rising' },
    { Component: LogoC, label: 'Spiral' },
    { Component: LogoE, label: 'Saturn' },
  ];

  // Duplicate for seamless loop
  const loopItems = [...options, ...options];

  const trackRef = useRef(null);
  const posRef   = useRef(null); // current translateX in px
  const rafRef   = useRef(null);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    // Speed: px per frame at 60 fps
    const SPEED = 0.22;

    // The true loop period = offsetLeft of the first item of copy 2.
    // scrollWidth/2 is WRONG because it doesn't account for the flex gap
    // that also sits between the two copies. offsetLeft gives the exact pixel
    // distance we need to subtract on reset so the content lines up perfectly.
    const period = track.children[options.length].offsetLeft;

    // Set immediately so track never flashes at translateX(0)
    posRef.current = -period;
    track.style.transform = `translateX(${posRef.current}px)`;

    const animate = () => {
      posRef.current += SPEED;
      if (posRef.current >= 0) posRef.current -= period;
      track.style.transform = `translateX(${posRef.current}px)`;
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const labelColor = heroMode
    ? 'rgba(245, 237, 227, 0.35)'
    : 'rgba(26, 20, 16, 0.35)';

  return (
    <div style={{ overflow: 'hidden', padding: '24px 0 8px', width: '100%' }}>
      <div
        ref={trackRef}
        style={{
          display: 'flex',
          gap: '64px',
          alignItems: 'center',
          width: 'max-content',
          willChange: 'transform',
        }}
      >
        {loopItems.map(({ Component, label }, i) => (
          <div key={`${label}-${i}`} style={{ textAlign: 'center' }}>
            <Component size={68} color="#c9956e" />
            <div style={{
              marginTop: '12px',
              fontSize: '9px',
              letterSpacing: '2px',
              textTransform: 'uppercase',
              color: labelColor,
            }}>
              {label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default LogoC;
